// The undo history, ported from the portal's hooks/usePlanUndo.ts
// (Elements-Portals src/modules/student/degree-plan/hooks/usePlanUndo.ts). Copy, don't share.
//
// A short history of plan edits that can be reversed exactly. The portal keeps closures; Redux keeps data, so each
// entry describes its undo and redo as operations (undoOps.js runs them):
//   { id, label: { key, fallback, options }, undo: Op, redo: Op, ids: (number|null)[] }
// `ids` are the trackers: a course put back after a removal is a new row with a new id, so an entry learns the id it
// is currently about from the plan each add returns. A tracker left at null means the id could not be told with
// certainty, and the operation that needs it then does nothing rather than act on the wrong course.
//
// The history lives as long as the plan is on screen. Elements owns the plan and someone else may have changed it
// since, so each entry is replayed as a fresh request and validated like any other edit, never written back as
// remembered state.
import { PB_UNDO_RECORD, PB_UNDO_REMAINING, PB_UNDO_STEP_BACK, PB_UNDO_STEP_FORWARD, PB_UNDO_TRACK } from './types';

export const UNDO_LIMIT = 20;

export const emptyHistory = Object.freeze({ past: [], future: [] });

const track = (entries, { entryId, slot, id }) =>
  entries.map((entry) => {
    if (entry.id !== entryId) return entry;
    const ids = [...entry.ids];
    ids[slot] = id;
    return { ...entry, ids };
  });

export function undoHistory(history = emptyHistory, action) {
  switch (action.type) {
    case PB_UNDO_RECORD:
      return { past: [...history.past, action.payload].slice(-UNDO_LIMIT), future: [] };
    case PB_UNDO_TRACK:
      return { past: track(history.past, action), future: track(history.future, action) };
    case PB_UNDO_REMAINING:
      return {
        past: history.past.map((entry) => (entry.id === action.entryId ? { ...entry, remainder: { side: action.side, op: action.op } } : entry)),
        future: history.future.map((entry) => (entry.id === action.entryId ? { ...entry, remainder: { side: action.side, op: action.op } } : entry)),
      };
    // With `entryId` (undoOps, after Elements took the entry), only that entry moves, and only from the end it was on.
    case PB_UNDO_STEP_BACK: {
      if (history.past.length === 0) return history;
      const entry = history.past[history.past.length - 1];
      if (action.entryId !== undefined && entry.id !== action.entryId) return history;
      return { past: history.past.slice(0, -1), future: [entry.remainder ? { ...entry, remainder: null } : entry, ...history.future] };
    }
    case PB_UNDO_STEP_FORWARD: {
      if (history.future.length === 0) return history;
      const [entry, ...rest] = history.future;
      if (action.entryId !== undefined && entry.id !== action.entryId) return history;
      return { past: [...history.past, entry.remainder ? { ...entry, remainder: null } : entry].slice(-UNDO_LIMIT), future: rest };
    }
    default:
      return history;
  }
}

/** The id an entry's tracker holds now, or null. */
export function trackedId(history, entryId, slot) {
  const entry = [...history.past, ...history.future].find((candidate) => candidate.id === entryId);
  const id = entry ? entry.ids[slot] : null;
  return id === undefined ? null : id;
}
